import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { GetAdDetailsResponse } from '@/api/types/ads.types';
import { GET_AD_DETAILS } from '@/api/queries/ads.queries';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { usePageType } from '@/hooks/usePageType/usePageType';
import { PageType } from '@/hooks/usePageType/usePageType.types';

const usePreviousRoute = () => {
  const { asPath } = useRouter();
  const ref = useRef<string | null>(null);
  const previousRef = useRef<string | null>(null);

  useEffect(() => {
    previousRef.current = ref.current;
    ref.current = asPath;
  }, [asPath]);

  return previousRef.current;
};

export function useAdDetails({ skipQuery = false } = {}) {
  const {
    query: { slug },
    asPath,
  } = useRouter();
  const pageType = usePageType();
  const source = usePlatformSource();
  const previousAsPath = usePreviousRoute();

  const legacyId = slug?.[2];

  const { data, loading, refetch, previousData } =
    useQuery<GetAdDetailsResponse>(GET_AD_DETAILS, {
      variables: {
        legacyId,
        source,
      },
      errorPolicy: 'all',
      // First request uses cache (if has SSR data)
      fetchPolicy: 'cache-first',
      // Next requests always make network request (don't use cache, because we have to track every ad view)
      nextFetchPolicy: 'network-only',
      skip:
        !legacyId ||
        pageType === PageType.NewAd ||
        pageType !== PageType.Ad ||
        skipQuery,
    });

  useEffect(() => {
    if (previousAsPath === null) {
      return;
    }

    if (previousAsPath !== asPath && legacyId) {
      // Refetch even if ad is in cache, because we have to track every ad view
      refetch();
    }
  }, [asPath, previousAsPath, legacyId, refetch]);

  return { adDetails: data?.adDetails, loading, refetch, previousData };
}
